import React, { useState, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved,
  faCommentSms,
  faPhone,
  faComments,
  faBriefcase,
  faKey,
  faTriangleExclamation,
  faCheckCircle,
  faTimesCircle,
  faArrowRight,
  faRotateRight,
  faTrophy,
  faLightbulb,
  faMagnifyingGlassChart,
  faTruck,
  faBuildingColumns,
  faReceipt,
  faLaptop,
  faHeart,
  faGift,
  faCreditCard,
  faEnvelope,
  faHouse,
} from "@fortawesome/free-solid-svg-icons";
import "./css/LandingPage-Quiz.css";

// ─────────────────────────────────────────────────────────────────────────────
// Number of questions shown per visit (randomly drawn from the pool below)
// ─────────────────────────────────────────────────────────────────────────────
const QUIZ_LENGTH = 6;

// ─────────────────────────────────────────────────────────────────────────────
// Quiz data — realistic, recent (2024–2026) scam techniques
// Each question shows a real-world message and asks the user to judge it.
// A random subset of QUIZ_LENGTH questions is drawn from this pool on each visit.
// answer: "scam" | "legit"
// ─────────────────────────────────────────────────────────────────────────────
const QUESTION_POOL = [
  {
    id: "toll",
    channel: "Text message",
    icon: faCommentSms,
    sender: "ExpressToll Notice",
    body: "Our records show an unpaid toll of $6.99. To avoid a $50.00 late fee and a report to the DMV, settle today: ez-tollpay-us.com/pay",
    answer: "scam",
    verdictTitle: "Scam — “Unpaid toll” smishing",
    flags: [
      "A tiny amount ($6.99) paired with a scary penalty ($50 + DMV report) to rush you.",
      "The link is a look‑alike domain, not the toll agency's official .gov site.",
      "Real toll agencies don't text random payment links to people they can't verify.",
    ],
    tip: "Never tap the link. Go directly to your toll authority's official website or app to check your balance.",
  },
  {
    id: "twofa",
    channel: "Text message",
    icon: faKey,
    sender: "KinBank",
    body: "Right after you tap “Log in” on your KinBank app, a text arrives: “Your KinBank verification code is 481920. It expires in 10 minutes. We will never ask you to share this code.”",
    answer: "legit",
    verdictTitle: "Legitimate — a code you requested",
    flags: [
      "It arrived right after you tapped “Log in” on the official app — you started it.",
      "It only shares a one‑time code and asks nothing of you: no links, no payment.",
      "It explicitly reminds you it will never ask you to share the code.",
    ],
    tip: "Genuine 2FA codes are safe — but never read one aloud to anyone who calls you claiming to be “the bank.”",
  },
  {
    id: "bank-callback",
    channel: "Text, then a phone call",
    icon: faPhone,
    sender: "FreedomBank Fraud Alert",
    body: "Did you attempt a $980.00 Zelle transfer to J. RIVERA? Reply NO to stop it. — You reply NO, and seconds later “the fraud department” calls asking you to move your money to a “safe account.”",
    answer: "scam",
    verdictTitle: "Scam — bank impersonation callback",
    flags: [
      "Replying triggers a call from a spoofed number that looks like your bank.",
      "No real bank ever tells you to move money to a “safe account.” That IS the theft.",
      "Pressure to act immediately, on the same call, before you can think.",
    ],
    tip: "Hang up. Call the number printed on the back of your card and verify independently.",
  },
  {
    id: "voice-clone",
    channel: "Phone call",
    icon: faPhone,
    sender: "Unknown number — sounds like family",
    body: "A panicked voice that sounds exactly like your grandson: “Grandma, I was in a crash and I'm in jail. Please don't tell Mom. The lawyer needs $2,000 in gift cards for bail right now.”",
    answer: "scam",
    verdictTitle: "Scam — AI voice‑cloning “emergency”",
    flags: [
      "AI can clone a familiar voice from just a few seconds of audio online.",
      "Urgency plus secrecy (“don't tell Mom”) is designed to stop you verifying.",
      "Bail, fees, or fines are NEVER paid with gift cards — that's always a scam.",
    ],
    tip: "Hang up and call the real person on a number you already know. Agree on a family “safe word” for emergencies.",
  },
  {
    id: "job-task",
    channel: "WhatsApp message",
    icon: faBriefcase,
    sender: "+1 (415) 555‑0143 “Recruiter”",
    body: "Hi! I'm hiring for a remote role: earn $150–$500/day completing simple app tasks — no experience needed. Start today, just deposit $30 to activate your earning account.",
    answer: "scam",
    verdictTitle: "Scam — “task”/job scam",
    flags: [
      "Unsolicited offer with pay that's far too high for trivial “tasks.”",
      "You're asked to pay (“deposit $30”) to start earning — the classic tell.",
      "A real employer never requires you to send money to get a job.",
    ],
    tip: "Legitimate jobs pay you. If you must pay to work, walk away.",
  },
  {
    id: "pig-butchering",
    channel: "Direct message",
    icon: faComments,
    sender: "A friendly new contact",
    body: "Someone you've been chatting with for weeks: “My crypto platform returned 30% this month. Let me coach you — start with just $500 on this exclusive app. You can withdraw anytime.”",
    answer: "scam",
    verdictTitle: "Scam — “pig‑butchering” investment fraud",
    flags: [
      "A near‑stranger promising guaranteed, unusually high returns.",
      "An “exclusive” app found outside the official app stores.",
      "Small buy‑in that appears to grow — then withdrawals get blocked or “taxed.”",
    ],
    tip: "Never invest through someone who contacted you online. Verify platforms with your regulator before sending a cent.",
  },
  {
    id: "package-redelivery",
    channel: "Text message",
    icon: faTruck,
    sender: "USPS Delivery",
    body: "USPS: Your package could not be delivered due to an incomplete address. Update your details within 24 hours to reschedule: usps-redelivery.info/track",
    answer: "scam",
    verdictTitle: "Scam — package “redelivery” smishing",
    flags: [
      "USPS does not text tracking links unless you signed up for alerts first.",
      "The link is a look‑alike domain, not the official usps.com site.",
      "A 24‑hour deadline is manufactured urgency to make you act without thinking.",
    ],
    tip: "Track packages only through the carrier's official app or by typing usps.com yourself.",
  },
  {
    id: "irs-threat",
    channel: "Phone call",
    icon: faBuildingColumns,
    sender: "“IRS Enforcement”",
    body: "This is the IRS. You owe back taxes and a warrant has been issued for your arrest. To avoid police at your door today, pay immediately using gift cards or a wire transfer.",
    answer: "scam",
    verdictTitle: "Scam — IRS impersonation",
    flags: [
      "The IRS first contacts you by mail — never a surprise call threatening arrest.",
      "Government agencies never accept gift cards or demand same‑day wire transfers.",
      "Threats of immediate arrest are pure pressure to stop you verifying.",
    ],
    tip: "Hang up. Check any real tax matter at irs.gov or call the IRS directly.",
  },
  {
    id: "amazon-order",
    channel: "Email",
    icon: faReceipt,
    sender: "Amazon Orders",
    body: "Thank you for your order! A $1,299.99 iPhone will ship to an address in another state. If you did not authorize this, call our support line now: 1‑888‑555‑0142.",
    answer: "scam",
    verdictTitle: "Scam — fake order confirmation",
    flags: [
      "A scary charge for something you didn't buy makes you panic and call.",
      "The “support” number connects to the scammer, not Amazon.",
      "They'll ask for remote access or card details to “refund” you.",
    ],
    tip: "Never call numbers inside suspicious emails. Check orders in the official Amazon app.",
  },
  {
    id: "tech-support-popup",
    channel: "Computer pop‑up",
    icon: faLaptop,
    sender: "“Microsoft Security”",
    body: "WARNING! Your computer is infected with 5 viruses. Do not shut down. Call Microsoft support at 1‑800‑555‑0177 immediately to prevent data loss.",
    answer: "scam",
    verdictTitle: "Scam — tech‑support pop‑up",
    flags: [
      "Real virus warnings never include a phone number to call.",
      "Microsoft and Apple do not monitor your PC and call you about infections.",
      "They want remote access to “fix” it — then they steal or lock your files.",
    ],
    tip: "Close the browser. If it won't close, restart. Never call the number on the pop‑up.",
  },
  {
    id: "romance",
    channel: "Dating app message",
    icon: faHeart,
    sender: "Someone you matched with",
    body: "After weeks of sweet messages: “My darling, I'd fly to meet you but my funds are frozen overseas. Could you send $800 for the flight? I'll pay you back the moment I land.”",
    answer: "scam",
    verdictTitle: "Scam — romance scam",
    flags: [
      "Strong affection fast, but they always have a reason they can't meet.",
      "The relationship reaches a crisis that only your money can solve.",
      "Requests for wires, gift cards, or crypto are the classic tell.",
    ],
    tip: "Never send money to someone you haven't met in person. Do a reverse‑image search of their photos.",
  },
  {
    id: "gift-card-boss",
    channel: "Email / Text",
    icon: faGift,
    sender: "“Your Manager”",
    body: "Hi, are you at your desk? I'm in a meeting and need a quick favor. Please buy five $100 Apple gift cards for a client and send me the codes. I'll reimburse you today.",
    answer: "scam",
    verdictTitle: "Scam — boss/CEO gift‑card fraud",
    flags: [
      "The message comes from a look‑alike address, not your manager's real one.",
      "Urgency plus secrecy (“I'm in a meeting”) prevents you from checking.",
      "No real workplace buys gift cards and texts codes as “payment.”",
    ],
    tip: "Verify any money request in person or on a known number before acting.",
  },
  {
    id: "netflix-payment",
    channel: "Email",
    icon: faCreditCard,
    sender: "“Netflix Billing”",
    body: "We couldn't process your payment. Your account will be suspended in 24 hours. Update your billing details here: netflix-account-verify.com/login",
    answer: "scam",
    verdictTitle: "Scam — subscription phishing",
    flags: [
      "The link goes to a look‑alike domain, not netflix.com.",
      "Threat of suspension in 24 hours is manufactured urgency.",
      "The page harvests your login and card details.",
    ],
    tip: "Never update billing from an email link. Log in by typing the site's address yourself.",
  },
  {
    id: "lottery-prize",
    channel: "Text message",
    icon: faGift,
    sender: "“National Sweepstakes”",
    body: "CONGRATULATIONS! You've won $850,000 in the Mega Prize draw. To release your winnings, pay a small $250 processing fee via gift card. Reply CLAIM to begin.",
    answer: "scam",
    verdictTitle: "Scam — advance‑fee lottery",
    flags: [
      "You can't win a lottery you never entered.",
      "Real prizes never require you to pay a fee up front to collect them.",
      "Gift‑card “fees” are untraceable — that's why scammers ask for them.",
    ],
    tip: "If you have to pay to receive a prize, it's a scam. Delete it.",
  },
  {
    id: "utility-shutoff",
    channel: "Phone call",
    icon: faPhone,
    sender: "“City Power & Light”",
    body: "Your electricity will be disconnected in 30 minutes for non‑payment. To keep your power on, pay your overdue balance right now with a prepaid debit card.",
    answer: "scam",
    verdictTitle: "Scam — utility shut‑off threat",
    flags: [
      "Utilities send written notice well before any disconnection.",
      "A 30‑minute deadline is designed to panic you into paying.",
      "No real utility demands prepaid cards or crypto.",
    ],
    tip: "Hang up and call the number on your actual bill to check your account.",
  },
  {
    id: "student-loan",
    channel: "Text message",
    icon: faBuildingColumns,
    sender: "“Loan Forgiveness Dept.”",
    body: "Good news! You qualify for full student‑loan forgiveness under a new program. Enroll before today's deadline and pay a one‑time $199 processing fee: loan-relief-now.co",
    answer: "scam",
    verdictTitle: "Scam — student‑loan forgiveness fraud",
    flags: [
      "Official loan servicers never charge a fee to apply for forgiveness.",
      "“Today's deadline” pressure and a look‑alike link are red flags.",
      "They collect your fee and your loan login credentials.",
    ],
    tip: "Manage federal loans only at studentaid.gov — applying there is always free.",
  },
  {
    id: "paypal-invoice",
    channel: "Email",
    icon: faReceipt,
    sender: "PayPal",
    body: "You sent an invoice request: a stranger has billed you $499.00 for “Bitcoin purchase.” Call 1‑877‑555‑0198 to dispute this charge immediately.",
    answer: "scam",
    verdictTitle: "Scam — fake invoice",
    flags: [
      "Anyone can send a PayPal invoice — receiving one doesn't mean you owe it.",
      "The “dispute” number leads to the scammer, not PayPal.",
      "They'll walk you into granting access or paying to “reverse” it.",
    ],
    tip: "Ignore the phone number. Just log in to PayPal and cancel or report the invoice.",
  },
  {
    id: "ssa-suspended",
    channel: "Phone call",
    icon: faPhone,
    sender: "“Social Security Admin.”",
    body: "Your Social Security number has been suspended due to suspicious activity linked to your name. Press 1 now to speak with an officer and verify your identity.",
    answer: "scam",
    verdictTitle: "Scam — SSN suspension",
    flags: [
      "Social Security numbers are never “suspended” — that isn't a real thing.",
      "The SSA won't robocall threatening you or ask you to confirm your SSN.",
      "They want your number and personal details for identity theft.",
    ],
    tip: "Hang up. Never confirm your SSN to an unexpected caller.",
  },
  {
    id: "crypto-giveaway",
    channel: "Social media post",
    icon: faComments,
    sender: "“Verified” celebrity account",
    body: "To celebrate, I'm giving back! Send any amount of Bitcoin to this address and I'll send DOUBLE back instantly. First 500 people only — don't miss out!",
    answer: "scam",
    verdictTitle: "Scam — crypto doubling giveaway",
    flags: [
      "No one doubles your money for free — it's mathematically a trap.",
      "The account is a look‑alike or hacked impersonation.",
      "Crypto sent to their address is gone forever — no reversals.",
    ],
    tip: "“Send crypto, get more back” is always a scam. Never send to giveaway addresses.",
  },
  {
    id: "order-shipped",
    channel: "Email",
    icon: faTruck,
    sender: "Amazon",
    body: "Your order has shipped! The wireless headphones you bought on Tuesday are on the way and arrive Friday. Track your package in the Amazon app.",
    answer: "legit",
    verdictTitle: "Legitimate — a shipment you're expecting",
    flags: [
      "It matches a purchase you actually made this week.",
      "It asks for nothing — no payment, no login, no urgent link.",
      "It points you to the official app rather than a random domain.",
    ],
    tip: "Still safest to open the Amazon app directly rather than clicking email buttons.",
  },
  {
    id: "appointment-reminder",
    channel: "Text message",
    icon: faCommentSms,
    sender: "Downtown Dental",
    body: "Reminder: you have a cleaning with Dr. Lee on Thursday at 2:00 PM. Reply C to confirm or R to reschedule. Call us at the number on your card with any questions.",
    answer: "legit",
    verdictTitle: "Legitimate — appointment reminder",
    flags: [
      "It references a real appointment you booked with a provider you know.",
      "It asks only for a simple confirm/reschedule reply — no money or codes.",
      "There's no threat, no urgency, and no suspicious link.",
    ],
    tip: "If unsure, call the office using the number you already have, not one in the text.",
  },
  {
    id: "parking-qr",
    channel: "QR code on a parking meter",
    icon: faCommentSms,
    sender: "Sticker over the real meter",
    body: "Scan to pay for parking! A QR sticker on the meter opens a site asking for your card number, CVV, and billing ZIP to “start your session.”",
    answer: "scam",
    verdictTitle: "Scam — malicious QR (“quishing”)",
    flags: [
      "Scammers stick fake QR codes over real ones to harvest cards.",
      "The site asks for full card details rather than using a known payment app.",
      "The web address doesn't match the city or official parking vendor.",
    ],
    tip: "Pay with the city's official parking app or the meter's card reader, not a random QR.",
  },
  {
    id: "antivirus-renewal",
    channel: "Email",
    icon: faEnvelope,
    sender: "“Norton Subscription”",
    body: "Your Norton 360 auto‑renewal of $429.99 was successful. If you did not authorize this, call 1‑866‑555‑0123 within 24 hours for a full refund.",
    answer: "scam",
    verdictTitle: "Scam — antivirus renewal refund",
    flags: [
      "A big surprise charge makes you call the “refund” line in a panic.",
      "The number reaches scammers who request remote access to “process” it.",
      "They then move money out while pretending to refund you.",
    ],
    tip: "Never call refund numbers in unexpected emails. Check charges with your bank directly.",
  },
  {
    id: "charity-disaster",
    channel: "Phone call",
    icon: faHeart,
    sender: "“Disaster Relief Fund”",
    body: "We're collecting for families hit by last week's hurricane. Every dollar helps — can we take a donation by gift card or wire transfer right now while lines are open?",
    answer: "scam",
    verdictTitle: "Scam — fake charity",
    flags: [
      "Scammers exploit real disasters with urgent, unsolicited calls.",
      "Legitimate charities don't ask for gift cards or wire transfers.",
      "High‑pressure “donate now” tactics discourage you from checking them.",
    ],
    tip: "Give only to charities you look up yourself and verify at sites like charitynavigator.org.",
  },
  {
    id: "bank-statement",
    channel: "Email",
    icon: faEnvelope,
    sender: "FreedomBank",
    body: "Your monthly statement is ready. Log in to online banking to view it. For your security, we've included no links — just visit our website or app as usual.",
    answer: "legit",
    verdictTitle: "Legitimate — routine statement notice",
    flags: [
      "It creates no urgency and makes no threats.",
      "It deliberately avoids links and tells you to visit the site yourself.",
      "It asks for no login, payment, or personal details in the message.",
    ],
    tip: "Good habit confirmed: reach your bank by typing its address or opening its app.",
  },
  {
    id: "overpayment-check",
    channel: "Marketplace message",
    icon: faReceipt,
    sender: "A “buyer” for your listing",
    body: "I'll take the couch! I accidentally sent a check for $1,500 instead of $500. So sorry — just deposit it and send the extra $1,000 back to my mover via Zelle.",
    answer: "scam",
    verdictTitle: "Scam — overpayment / fake check",
    flags: [
      "The check is fake and will bounce days after you deposit it.",
      "You send real money back before the bank discovers the check is bad.",
      "An “accidental” overpayment plus a refund request is the whole scheme.",
    ],
    tip: "Never accept an overpayment and wire back the difference. Wait for funds to truly clear.",
  },
  {
    id: "account-locked",
    channel: "Email",
    icon: faEnvelope,
    sender: "“Apple ID Security”",
    body: "Your Apple ID has been locked due to unusual sign‑in activity. Verify your identity within 12 hours to avoid permanent deletion: apple-id-unlock.support/verify",
    answer: "scam",
    verdictTitle: "Scam — account‑lock phishing",
    flags: [
      "The link is a look‑alike domain, not apple.com.",
      "A 12‑hour “or it's deleted” threat is manufactured urgency.",
      "The page steals your Apple ID and password.",
    ],
    tip: "Check account status only in Settings on your device or at the official apple.com.",
  },
  {
    id: "rental-deposit",
    channel: "Rental listing reply",
    icon: faHouse,
    sender: "“Landlord” of a cheap apartment",
    body: "The apartment is available and priced low because I moved abroad. I can't show it in person, but wire a $900 deposit today and I'll mail the keys to hold it for you.",
    answer: "scam",
    verdictTitle: "Scam — rental / deposit fraud",
    flags: [
      "A price that's too good, plus a landlord who conveniently can't meet.",
      "You're asked to wire a deposit before ever seeing the unit.",
      "The listing photos are often stolen from a real listing elsewhere.",
    ],
    tip: "Never pay a deposit before touring a place and verifying the owner. Never wire funds.",
  },
  {
    id: "wrong-number",
    channel: "Text message",
    icon: faComments,
    sender: "Unknown number",
    body: "Hi Jessica, are we still on for lunch? — You reply that they have the wrong number, and they keep chatting warmly, eventually steering toward a “great crypto tip.”",
    answer: "scam",
    verdictTitle: "Scam — “wrong number” opener",
    flags: [
      "A friendly “wrong number” is a scripted way to start a conversation.",
      "They invest time building rapport before mentioning investments.",
      "It leads into a “pig‑butchering” crypto scheme.",
    ],
    tip: "Don't keep chatting with wrong‑number strangers. Just stop replying and block.",
  },
  {
    id: "medicare-card",
    channel: "Phone call",
    icon: faPhone,
    sender: "“Medicare Services”",
    body: "We're issuing new plastic Medicare cards with a chip. To mail yours, please confirm your Medicare number, date of birth, and bank details for the small shipping fee.",
    answer: "scam",
    verdictTitle: "Scam — Medicare card fraud",
    flags: [
      "Medicare never calls out of the blue asking you to confirm your number.",
      "There's no fee for a Medicare card, and no “chip card” program requiring payment.",
      "They're harvesting your Medicare and bank details for fraud.",
    ],
    tip: "Hang up. Never give your Medicare number to an unexpected caller. Call 1‑800‑MEDICARE yourself.",
  },
  {
    id: "legit-fraud-alert",
    channel: "Text message",
    icon: faCreditCard,
    sender: "Your card issuer",
    body: "Did you attempt a $214.30 purchase at BestBuy.com? Reply YES if this was you or NO if it wasn't. We will never ask you to move money or share a code.",
    answer: "legit",
    verdictTitle: "Legitimate — a genuine fraud check",
    flags: [
      "It only asks a simple YES/NO — it doesn't push you to move money.",
      "It states plainly it will never ask you to transfer funds or share codes.",
      "There's no link to click and no request for personal details.",
    ],
    tip: "Safe to reply YES/NO — but if a “rep” then calls asking you to move money, that's the scam.",
  },
  {
    id: "jury-duty",
    channel: "Phone call",
    icon: faBuildingColumns,
    sender: "“County Sheriff's Office”",
    body: "You failed to appear for jury duty and a warrant is out for your arrest. You can avoid jail by paying the fine today with a prepaid card — stay on the line while we process it.",
    answer: "scam",
    verdictTitle: "Scam — jury‑duty warrant",
    flags: [
      "Courts handle missed jury duty by mail, not surprise arrest threats.",
      "No real agency takes fines by prepaid card over the phone.",
      "“Stay on the line” keeps you from hanging up to verify.",
    ],
    tip: "Hang up and call the actual court clerk's office using a number you look up yourself.",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Fisher–Yates shuffle, then take the first `count` for a fresh set each visit
// ─────────────────────────────────────────────────────────────────────────────
function getRandomQuestions(count) {
  const pool = [...QUESTION_POOL];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}

// ─────────────────────────────────────────────────────────────────────────────
// Result tiers based on final score
// ─────────────────────────────────────────────────────────────────────────────
function getResultTier(score, total) {
  const pct = score / total;
  if (pct === 1) {
    return {
      title: "Scam‑Spotting Pro",
      message:
        "Flawless. You caught every trick — including the message that was perfectly safe. Your instincts are sharp.",
    };
  }
  if (pct >= 0.66) {
    return {
      title: "Sharp Eye",
      message:
        "Strong work. You spot most scams — a little extra caution on the trickiest ones will make you nearly untouchable.",
    };
  }
  if (pct >= 0.34) {
    return {
      title: "Getting There",
      message:
        "You're building the instinct. Today's scams are engineered to fool careful people — a few habits will close the gap fast.",
    };
  }
  return {
    title: "Stay Alert",
    message:
      "These scams are convincing by design — you're not alone. The good news: recognizing the patterns is a skill you can learn.",
  };
}

function LandingPageQuiz({ onManageAccountClick }) {
  // Draw a fresh random set of questions once per visit (stable across re-renders)
  const [questions, setQuestions] = useState(() =>
    getRandomQuestions(QUIZ_LENGTH),
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selected, setSelected] = useState(null); // "scam" | "legit"
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);

  const total = questions.length;
  const question = questions[currentIndex];
  const isCorrect = answered && selected === question.answer;

  const tier = useMemo(
    () => (finished ? getResultTier(score, total) : null),
    [finished, score, total],
  );

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    document.documentElement.scrollTop = 0;
  };

  const handleAnswer = useCallback(
    (choice) => {
      if (answered) return;
      setSelected(choice);
      setAnswered(true);
      if (choice === question.answer) {
        setScore((s) => s + 1);
      }
    },
    [answered, question],
  );

  const handleNext = useCallback(() => {
    if (currentIndex + 1 >= total) {
      setFinished(true);
      return;
    }
    setCurrentIndex((i) => i + 1);
    setSelected(null);
    setAnswered(false);
  }, [currentIndex, total]);

  const handleRestart = useCallback(() => {
    setQuestions(getRandomQuestions(QUIZ_LENGTH));
    setCurrentIndex(0);
    setSelected(null);
    setAnswered(false);
    setScore(0);
    setFinished(false);
  }, []);

  const progressPct = finished ? 100 : Math.round((currentIndex / total) * 100);

  return (
    <section className="quiz-section" aria-label="Spot the Scam quiz">
      <div className="scroll-to-top-link" onClick={scrollToTop}>
        Top ↑
      </div>

      {/* Header */}
      <div className="quiz-header">
        <h2 className="quiz-title">
          <FontAwesomeIcon
            icon={faMagnifyingGlassChart}
            className="quiz-title-icon"
          />
          Can You Spot the Scam?
        </h2>
        <p className="quiz-subtitle">
          Six real‑world messages using the tactics fraudsters are running right
          now — from AI voice clones to “unpaid toll” texts. Trust your gut,
          then see how you did.
        </p>
      </div>

      <div className="quiz-card" role="region" aria-live="polite">
        {!finished ? (
          <>
            {/* Progress */}
            <div className="quiz-progress-row">
              <span className="quiz-progress-label">
                Question {currentIndex + 1} of {total}
              </span>
              <span className="quiz-progress-score">
                Score: {score}/{total}
              </span>
            </div>
            <div className="quiz-progress-track">
              <div
                className="quiz-progress-fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            {/* Scenario message */}
            <div className="quiz-message" data-channel={question.channel}>
              <div className="quiz-message-header">
                <FontAwesomeIcon
                  icon={question.icon}
                  className="quiz-message-icon"
                />
                <div className="quiz-message-meta">
                  <span className="quiz-message-channel">
                    {question.channel}
                  </span>
                  <span className="quiz-message-sender">{question.sender}</span>
                </div>
              </div>
              <p className="quiz-message-body">{question.body}</p>
            </div>

            {/* Question prompt */}
            <p className="quiz-prompt">
              Is this a scam or a legitimate message?
            </p>

            {/* Answer buttons */}
            <div className="quiz-options">
              <button
                type="button"
                className={`quiz-option quiz-option-scam ${
                  answered && selected === "scam" ? "selected" : ""
                } ${
                  answered && question.answer === "scam" ? "is-correct" : ""
                } ${
                  answered && selected === "scam" && question.answer !== "scam"
                    ? "is-wrong"
                    : ""
                }`}
                onClick={() => handleAnswer("scam")}
                disabled={answered}
                aria-pressed={selected === "scam"}
              >
                <FontAwesomeIcon icon={faTriangleExclamation} />
                It's a scam
              </button>
              <button
                type="button"
                className={`quiz-option quiz-option-legit ${
                  answered && selected === "legit" ? "selected" : ""
                } ${
                  answered && question.answer === "legit" ? "is-correct" : ""
                } ${
                  answered &&
                  selected === "legit" &&
                  question.answer !== "legit"
                    ? "is-wrong"
                    : ""
                }`}
                onClick={() => handleAnswer("legit")}
                disabled={answered}
                aria-pressed={selected === "legit"}
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                It's legitimate
              </button>
            </div>

            {/* Feedback */}
            {answered && (
              <div
                className={`quiz-feedback ${isCorrect ? "correct" : "incorrect"}`}
              >
                <div className="quiz-feedback-verdict">
                  <FontAwesomeIcon
                    icon={isCorrect ? faCheckCircle : faTimesCircle}
                    className="quiz-feedback-verdict-icon"
                  />
                  <span>
                    {isCorrect ? "Correct — " : "Not quite — "}
                    {question.verdictTitle}
                  </span>
                </div>
                <ul className="quiz-feedback-flags">
                  {question.flags.map((flag, i) => (
                    <li key={i}>{flag}</li>
                  ))}
                </ul>
                <div className="quiz-feedback-tip">
                  <FontAwesomeIcon
                    icon={faLightbulb}
                    className="quiz-feedback-tip-icon"
                  />
                  <span>{question.tip}</span>
                </div>
                <button
                  type="button"
                  className="quiz-next-btn"
                  onClick={handleNext}
                >
                  {currentIndex + 1 >= total
                    ? "See my results"
                    : "Next message"}
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            )}
          </>
        ) : (
          /* Results screen */
          <div className="quiz-results">
            <div className="quiz-results-badge">
              <FontAwesomeIcon icon={faTrophy} />
            </div>
            <div className="quiz-results-score">
              {score}
              <span className="quiz-results-score-total">/{total}</span>
            </div>
            <h3 className="quiz-results-title">{tier.title}</h3>
            <p className="quiz-results-message">{tier.message}</p>

            <div className="quiz-results-takeaway">
              <FontAwesomeIcon
                icon={faMagnifyingGlassChart}
                className="quiz-results-takeaway-icon"
              />
              <p>
                The tell is almost always the same: <strong>urgency</strong>,{" "}
                <strong>secrecy</strong>, or a{" "}
                <strong>request for money or codes</strong>. When you feel
                rushed, stop and verify through a channel you trust.
              </p>
            </div>

            <div className="quiz-results-actions">
              <button
                type="button"
                className="quiz-cta-primary"
                onClick={
                  onManageAccountClick ? onManageAccountClick : scrollToTop
                }
              >
                <FontAwesomeIcon icon={faShieldHalved} />
                Protect my family with KinShield
              </button>
              <button
                type="button"
                className="quiz-cta-secondary"
                onClick={handleRestart}
              >
                <FontAwesomeIcon icon={faRotateRight} />
                Try again
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="quiz-bottom-cta">
        <div className="quiz-bottom-cta-inner">
          <h3 className="quiz-bottom-cta-title">
            Scams are getting smarter. So should your defense.
          </h3>
          <p className="quiz-bottom-cta-text">
            KinShield watches for the tactics you just saw — voice clones, fake
            toll texts, and “urgent” bank calls — and steps in before your
            family loses a cent.
          </p>
          <div className="quiz-bottom-cta-actions">
            <button
              type="button"
              className="quiz-cta-primary"
              onClick={
                onManageAccountClick ? onManageAccountClick : scrollToTop
              }
            >
              <FontAwesomeIcon icon={faShieldHalved} />
              Protect my family with KinShield
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LandingPageQuiz;
